import {
  CheckoutAppSelector,
  ShippingOptionState,
  useAppSelector,
  useCheckoutData,
  useCrossBorderTrade,
  useInitialData,
  useIsExpress,
  useIsShippingOptionRequired,
  useOtbAmount,
  useSelectedPaymentOption,
} from '..'
import { AddressModeType, ShippingOption, ShippingOptionAddressErrors, ShippingOptionErrorType, ShippingOptionStatus } from '../../model'
import { isUndefinedOrEmpty } from '@afterpay/utils/string'
import { minDefined } from '../../component'
import { valueIsNotNullOrUndefined } from '../../utils/validation'

/* Selectors */
export const shippingOptionSelector: CheckoutAppSelector<ShippingOptionState> = (state) => state?.shippingOption

/* Hooks */
export const useShippingOption = (): ShippingOptionState => useAppSelector(shippingOptionSelector)

export const useIsShippingOptionValid = (): boolean => {
  const isExpress = useIsExpress()
  const { shippingOptionRequired, addressMode } = useInitialData()
  const { shippingOptionsStatus, error } = useShippingOption()
  const noShippingOptionErrors = isUndefinedOrEmpty(error)
  const allowedShippingOptions = useAllowedShippingOptions()
  // it is used in the case that consumers switch payment plans for PCL orders
  const isSelectedShippingOptionExist = useSelectedShippingOptionAllowed(allowedShippingOptions)

  if (!isExpress || addressMode === AddressModeType.NO_ADDRESS) {
    return true
  }

  if (isExpress && !shippingOptionRequired) {
    return noShippingOptionErrors
  }

  if (error === ShippingOptionErrorType.ShippingOptionUpdateInvalid) {
    return false
  }

  return shippingOptionsStatus === ShippingOptionStatus.UpdatedSelected && isSelectedShippingOptionExist
}

export const useIsShippingOptionLoading = (): boolean => {
  const { shippingOptionsStatus } = useShippingOption()

  return (
    shippingOptionsStatus === ShippingOptionStatus.UpdatingSelected ||
    shippingOptionsStatus === ShippingOptionStatus.Loading ||
    shippingOptionsStatus === ShippingOptionStatus.RequiresCheckoutUpdate
  )
}

export const useAllowedShippingOptions = (): ShippingOption[] => {
  const { allShippingOptions, xbtAllShippingOptions } = useShippingOption()
  const checkoutData = useCheckoutData()
  const { isCrossBorder } = useCrossBorderTrade()
  const otbAmount = useOtbAmount()
  const selectedPaymentOption = useSelectedPaymentOption()

  const shippingOptions = isCrossBorder ? xbtAllShippingOptions : allShippingOptions
  const maxLoanAmount = selectedPaymentOption?.maxLoanAmount
  const minLimitForPbi = minDefined(checkoutData?.maximumOrderValue?.amount, otbAmount?.amount)
  const minLimitForPcl = minDefined(checkoutData?.maximumOrderValue?.amount, maxLoanAmount?.amount)
  const minLimit = valueIsNotNullOrUndefined(maxLoanAmount) ? minLimitForPcl : minLimitForPbi
  return minLimit !== undefined
    ? shippingOptions.filter(({ orderAmount }) => {
        return minLimit >= Number(orderAmount.amount)
      })
    : shippingOptions
}

export const useSelectedShippingOptionAllowed = (allowedShippingOptions: ShippingOption[]): boolean => {
  const { selectedShippingOption } = useShippingOption()
  const { isCrossBorder } = useCrossBorderTrade()

  return (
    valueIsNotNullOrUndefined(selectedShippingOption) &&
    (isCrossBorder
      ? valueIsNotNullOrUndefined(allowedShippingOptions.find((_option) => _option.id === selectedShippingOption.id))
      : allowedShippingOptions.includes(selectedShippingOption))
  )
}

export const useIsShippingOptionNotSelected = (): boolean => {
  const isShippingOptionRequired = useIsShippingOptionRequired()
  const { shippingOptionsStatus } = useShippingOption()

  return isShippingOptionRequired && (shippingOptionsStatus < ShippingOptionStatus.UpdatingSelected || shippingOptionsStatus === ShippingOptionStatus.Error)
}

export const useIsShippingOptionAddressError = (): boolean => {
  const { error } = useShippingOption()
  const isShippingAddressError = ShippingOptionAddressErrors.includes(error as ShippingOptionErrorType)
  return isShippingAddressError
}
